import { Select, type SelectItemType } from "@admin-ui/components/base/select/select";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { CloseButton } from "@admin-ui/components/primitives/base/buttons/close-button";
import { FeaturedIcon } from "@admin-ui/components/primitives/foundations/featured-icon";
import type { DateValue } from "@internationalized/date";
import type { AdminAccountsMembersPersonSearchResult } from "@travily/types/models";
import { ArrowLeft, UserPlus01 } from "@untitledui/icons";
import type { Key } from "react";
import { Heading as AriaHeading } from "react-aria-components";
import { PersonFormFields } from "@/pages/admin/accounts/components/person-form-fields";

export type Mode = "search" | "create";

export interface NewViewProps {
  mode: Mode;
  canSubmit: boolean;
  onClose: () => void;
  onSubmit: () => void;
  /** Search mode */
  searchQuery: string;
  items: SelectItemType[];
  selectedPerson: AdminAccountsMembersPersonSearchResult | null;
  onSearchInputChange: (value: string) => void;
  onSelectionChange: (key: Key | null) => void;
  onClearSelectedPerson: () => void;
  onSwitchToCreate: () => void;
  /** Create mode */
  newName: string;
  newDateOfBirth: DateValue | null;
  onNameChange: (value: string) => void;
  onDateOfBirthChange: (value: DateValue | null) => void;
  onSwitchToSearch: () => void;
}

export function NewView({
  mode,
  canSubmit,
  onClose,
  onSubmit,
  searchQuery,
  items,
  selectedPerson,
  onSearchInputChange,
  onSelectionChange,
  onClearSelectedPerson,
  onSwitchToCreate,
  newName,
  newDateOfBirth,
  onNameChange,
  onDateOfBirthChange,
  onSwitchToSearch,
}: NewViewProps) {
  return (
    <div className="pointer-events-auto relative w-full overflow-hidden rounded-2xl bg-primary shadow-xl sm:max-w-120">
      <CloseButton
        onPress={onClose}
        theme="light"
        size="sm"
        className="absolute top-3 right-3 z-10 sm:top-4 sm:right-4"
      />

      <div className="flex flex-col gap-5 px-4 pt-5 sm:px-6 sm:pt-6">
        <div className="flex flex-col gap-4">
          <FeaturedIcon color="brand" size="md" theme="outline" icon={UserPlus01} />
          <div className="flex flex-col gap-1">
            <AriaHeading slot="title" className="text-lg font-semibold text-primary">
              Add member
            </AriaHeading>
            <p className="text-sm text-tertiary">
              {mode === "search" ? "Search for a person to add to this account." : "Create a new person."}
            </p>
          </div>
        </div>

        {mode === "search" && (
          <>
            <Select.ComboBox
              label="Search people"
              placeholder="Search by name or email..."
              size="sm"
              shortcut={false}
              items={items}
              inputValue={searchQuery}
              onInputChange={onSearchInputChange}
              onSelectionChange={onSelectionChange}
              defaultFilter={() => true}
              allowsCustomValue
            >
              {(item) => (
                <Select.Item id={item.id} supportingText={item.supportingText} isDisabled={item.isDisabled}>
                  {item.label}
                </Select.Item>
              )}
            </Select.ComboBox>

            {selectedPerson && (
              <div className="flex items-center gap-3 rounded-lg border border-brand-300 bg-brand-25 px-3 py-2.5">
                <div className="flex size-8 items-center justify-center rounded-full bg-brand-100 text-brand-600">
                  <UserPlus01 className="size-4" />
                </div>
                <div className="flex flex-1 flex-col gap-0.5">
                  <span className="text-sm font-medium text-primary">{selectedPerson.name}</span>
                  {selectedPerson.email && <span className="text-xs text-tertiary">{selectedPerson.email}</span>}
                </div>
                <button
                  type="button"
                  onClick={onClearSelectedPerson}
                  className="text-xs font-medium text-tertiary hover:text-primary"
                >
                  Clear
                </button>
              </div>
            )}

            <p className="text-sm text-tertiary">
              Person not listed?{" "}
              <button
                type="button"
                onClick={onSwitchToCreate}
                className="font-medium text-brand-600 hover:text-brand-700"
              >
                Create a new person instead
              </button>
            </p>
          </>
        )}

        {mode === "create" && (
          <>
            <PersonFormFields
              name={newName}
              onNameChange={onNameChange}
              dateOfBirth={newDateOfBirth}
              onDateOfBirthChange={onDateOfBirthChange}
            />

            <button
              type="button"
              onClick={onSwitchToSearch}
              className="flex items-center gap-1.5 text-sm font-medium text-tertiary hover:text-primary"
            >
              <ArrowLeft className="size-4" />
              Back to search
            </button>
          </>
        )}
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 border-t border-secondary p-4 sm:px-6">
        <Button size="md" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button size="md" color="primary" isDisabled={!canSubmit} onClick={onSubmit}>
          Add member
        </Button>
      </div>
    </div>
  );
}
